import React from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { formatCurrency, parseFloatFromString } from '../utils/formatters';
import { usePortfolioContext } from '../contexts/PortfolioContext';
import TransactionListItem from './TransactionListItem';
import { tokenRoutePath } from '../utils/tokenRoutes';

const MonthlyTransactionGroup = ({ month, transactions, onEdit, onDelete, tokenLocations = [] }) => {
  const navigate = useNavigate();
  const { currentPortfolio } = usePortfolioContext();
  const portfolioCurrency = currentPortfolio?.currency || 'USD';
  const formatMonthHeader = (dateString) => {
    try {
      return format(new Date(dateString), 'MMMM yyyy');
    } catch (error) {
      return 'Invalid date';
    }
  };

  const totals = React.useMemo(() => {
    return transactions.reduce((acc, { transaction }) => {
      const type = transaction?.transactionType;
      const currency = transaction?.currency || portfolioCurrency;
      const scaled = transaction?.scaledValues || {};
      const cost = scaled.cost != null
        ? parseFloatFromString(scaled.cost)
        : (currency === portfolioCurrency ? parseFloatFromString(transaction?.cost) : 0);
      const proceeds = scaled.proceeds != null
        ? parseFloatFromString(scaled.proceeds)
        : (currency === portfolioCurrency ? parseFloatFromString(transaction?.proceeds) : 0);
      if (type === 'BUY' && cost > 0) {
        acc.bought += cost;
      } else if (type === 'SELL' && proceeds > 0) {
        acc.sold += proceeds;
      }
      return acc;
    }, { bought: 0, sold: 0 });
  }, [transactions, portfolioCurrency]);

  return (
    <div className="mb-6">
      <div className="sticky top-0 z-10 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 py-3 px-3 sm:px-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          <span>{formatMonthHeader(month)}</span>
          <span className="block sm:inline sm:ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
            {(() => {
              const parts = [];
              if (totals.bought > 0) {
                parts.push(`Bought ${formatCurrency(totals.bought, portfolioCurrency)}`);
              }
              if (totals.sold > 0) {
                parts.push(`Sold ${formatCurrency(totals.sold, portfolioCurrency)}`);
              }
              return parts.length ? `· ${parts.join(' · ')}` : '';
            })()}
          </span>
        </h3>
      </div>
      
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {transactions.map((transactionWithToken) => (
          <div
            key={transactionWithToken.transaction.id}
            className="cursor-pointer"
            onClick={() => {
              const path = tokenRoutePath(transactionWithToken.token);
              if (path) navigate(path);
            }}
          >
            <TransactionListItem 
              transactionWithToken={transactionWithToken}
              onEdit={onEdit}
              onDelete={onDelete}
              tokenLocations={tokenLocations}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default MonthlyTransactionGroup; 